import { throttle } from "lodash-es";

export const LAST_ACTIVITY_KEY = "lastActivityTimestamp";
const AUTO_LOGOUT_MINUTES_KEY = "autoLogoutAfterMinutes";
const LOGOUT_CHANNEL_NAME = "inactivity-logout-channel";
const USER_EVENTS = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];

interface InactivityTimeoutOptions {
  timeoutMinutes: number;
  onTimeout: () => void;
}

// Utility function to check if user should be logged out based on stored values
export function checkShouldAutoLogout(): boolean {
  const timeoutMinutesStr = localStorage.getItem(AUTO_LOGOUT_MINUTES_KEY);
  const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);

  if (!timeoutMinutesStr || !lastActivityStr) {
    return false; // No timeout configured or no activity recorded
  }

  const timeoutMinutes = Number(timeoutMinutesStr);
  const lastActivity = Number(lastActivityStr);

  if (!timeoutMinutes || timeoutMinutes <= 0) {
    return false; // No timeout configured
  }

  const timeSinceLastActivity = Date.now() - lastActivity;
  const timeoutDuration = timeoutMinutes * 60 * 1000;

  return timeSinceLastActivity >= timeoutDuration;
}

// Utility function to clean up inactivity-related localStorage items
export function cleanupInactivityStorage(): void {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(AUTO_LOGOUT_MINUTES_KEY);
}

export function setupInactivityLogout({ timeoutMinutes, onTimeout }: InactivityTimeoutOptions) {
  if (!timeoutMinutes) return;

  localStorage.setItem(AUTO_LOGOUT_MINUTES_KEY, timeoutMinutes.toString());

  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const channel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);

  const throttledEventHandler = throttle(
    () => {
      // Only reset timer if localStorage items still exist (not cleaned up)
      if (localStorage.getItem(AUTO_LOGOUT_MINUTES_KEY)) {
        resetTimer();
      }
    },
    1000,
    { leading: true, trailing: false },
  );

  function cleanup() {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    window.removeEventListener("storage", handleStorageChange);
    USER_EVENTS.forEach((event) => {
      window.removeEventListener(event, throttledEventHandler);
    });
    cleanupInactivityStorage();
    channel.close();
  }

  function handleTimeout() {
    cleanup();
    const channel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
    channel.postMessage({ type: "LOGOUT" });
    channel.close();
    onTimeout();
  }

  function checkIfTimedOut() {
    if (checkShouldAutoLogout()) {
      handleTimeout();
      return true;
    }
    return false;
  }

  function resetTimer() {
    if (timeoutId) clearTimeout(timeoutId);

    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

    if (timeoutMinutes > 0) {
      timeoutId = setTimeout(handleTimeout, timeoutMinutes * 60 * 1000);
    }
  }

  function handleStorageChange(event: StorageEvent) {
    // Only handle storage changes if the system is still active (localStorage items exist)
    if (
      event.key === LAST_ACTIVITY_KEY &&
      localStorage.getItem(AUTO_LOGOUT_MINUTES_KEY) &&
      !checkIfTimedOut()
    ) {
      resetTimer();
    }
  }

  function handleBroadcastMessage(event: MessageEvent) {
    if (event.data.type === "LOGOUT") {
      cleanup();
      onTimeout();
    }
  }

  if (!checkIfTimedOut() && timeoutMinutes > 0) {
    resetTimer();
    window.addEventListener("storage", handleStorageChange);
    channel.addEventListener("message", handleBroadcastMessage);
    USER_EVENTS.forEach((event) => {
      window.addEventListener(event, throttledEventHandler);
    });
  }
}
